import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/contexts/AuthContext';
import { api, setAccessToken } from '../core/services/api';
import { playerSignIn } from '../core/services/playerAuthClient';
import { getDefaultRedirectPath } from '../core/utils/authHelpers';
import Alert from '../shared/components/ui/Alert';

export default function LoginPage() {
  const { t } = useTranslation('auth');

  // Tab: 'player' | 'admin'
  const [activeTab, setActiveTab] = useState('player');

  // Player login state
  const [memberNumber, setMemberNumber] = useState('');
  const [password, setPassword] = useState('');

  // Admin login state
  const [email, setEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginMode, setAdminLoginMode] = useState('password'); // 'password' | 'magic-link'
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Contact support dialog
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSent, setSupportSent] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);

  // Forgot password dialog
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotClubs, setForgotClubs] = useState([]);
  const [forgotClubId, setForgotClubId] = useState('');
  const [forgotMemberNumber, setForgotMemberNumber] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const { signInWithMagicLink, user, userRoles, rehydrate, rehydratePlayer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && userRoles.length > 0) {
      const from = location.state?.from?.pathname || getDefaultRedirectPath(userRoles);
      navigate(from, { replace: true });
    }
  }, [user, userRoles, navigate, location]);

  // ─── Player login ─────────────────────────────────────────────────────────────
  const handlePlayerSignIn = async (e) => {
    e.preventDefault();
    setError('');
    if (!memberNumber.trim()) { setError(t('login.error.memberNumberRequired')); return; }
    if (!password) { setError(t('login.error.passwordRequired')); return; }

    setLoading(true);
    try {
      const { data, error: signInError } = await playerSignIn(memberNumber, password);
      if (signInError) {
        const msg = signInError.message || '';
        if (msg.toLowerCase().includes('rate') || msg.toLowerCase().includes('too many')) {
          setError(t('login.error.tooManyAttempts'));
        } else if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
          setError(t('login.error.invalidCredentials'));
        } else {
          setError(t('login.error.loginFailed'));
        }
        return;
      }
      await rehydratePlayer();
    } catch {
      setError(t('login.error.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Admin magic-link ─────────────────────────────────────────────────────────
  const handleMagicLinkSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!email) { setError(t('login.error.emailRequired')); setLoading(false); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError(t('login.error.invalidEmail')); setLoading(false); return; }
    const { data, error } = await signInWithMagicLink(email);
    if (error) {
      setError(error.message || t('login.error.magicLinkFailed'));
    } else {
      setMagicLinkSent(true);
    }
    setLoading(false);
  };

  // ─── Admin password login ─────────────────────────────────────────────────────
  const handleAdminPasswordSignIn = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError(t('login.error.emailRequired')); return; }
    if (!adminPassword) { setError(t('login.error.passwordRequired')); return; }
    setLoading(true);
    try {
      const data = await api.post('/api/auth/login', { email, password: adminPassword });
      setAccessToken(data.access_token);
      await rehydrate();
    } catch {
      setError(t('login.error.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Contact support ──────────────────────────────────────────────────────────
  const handleContactSupport = async (e) => {
    e.preventDefault();
    if (!supportMessage.trim()) return;
    setSupportLoading(true);
    try {
      await api.post('/api/auth/contact-support', { message: supportMessage });
      setSupportSent(true);
    } catch {
      // fail silently — user sees no error to avoid frustration
    } finally {
      setSupportLoading(false);
    }
  };

  const openContactSupport = () => {
    setSupportMessage('');
    setSupportSent(false);
    setShowContactSupport(true);
  };

  // ─── Forgot password ──────────────────────────────────────────────────────────
  const openForgotPassword = async () => {
    setForgotMemberNumber('');
    setForgotClubId('');
    setForgotSent(false);
    setShowForgotPassword(true);
    if (forgotClubs.length === 0) {
      try {
        const clubs = await api.get('/api/auth/clubs-public');
        setForgotClubs(clubs || []);
      } catch {
        setForgotClubs([]);
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotClubId || !forgotMemberNumber.trim()) return;
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password-request', {
        club_id: forgotClubId,
        member_number: forgotMemberNumber,
      });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // always show success to avoid leaking info
    } finally {
      setForgotLoading(false);
    }
  };

  const tabClass = (tab) =>
    `flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
      activeTab === tab
        ? 'bg-white text-indigo-700 shadow-sm'
        : 'text-gray-500 hover:text-gray-700'
    }`;

  const inputClass = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
  const primaryBtn = 'w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('login.heading.title')}</h1>
          <p className="text-gray-600">{t('login.text.subtitle')}</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">

          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button className={tabClass('player')} onClick={() => { setActiveTab('player'); setError(''); }}>
              {t('login.tab.player')}
            </button>
            <button className={tabClass('admin')} onClick={() => { setActiveTab('admin'); setError(''); }}>
              {t('login.tab.admin')}
            </button>
          </div>

          {error && <Alert variant="error" className="mb-4 text-sm">{error}</Alert>}

          {/* ── Player tab ─────────────────────────────────────────────────── */}
          {activeTab === 'player' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-5">
                {t('login.heading.playerLogin')}
              </h2>

              <form onSubmit={handlePlayerSignIn} className="space-y-4">
                <div>
                  <label htmlFor="memberNumber" className="block text-sm font-medium text-gray-700">
                    {t('login.input.label.memberNumber')}
                  </label>
                  <input
                    id="memberNumber"
                    type="text"
                    autoComplete="username"
                    required
                    value={memberNumber}
                    onChange={(e) => setMemberNumber(e.target.value)}
                    className={inputClass}
                    placeholder={t('login.input.placeholder.memberNumber')}
                    disabled={loading}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    {t('login.input.label.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    placeholder={t('login.input.placeholder.password')}
                    disabled={loading}
                  />
                </div>

                <button type="submit" disabled={loading} className={primaryBtn}>
                  {loading ? t('login.button.signingIn') : t('login.button.signIn')}
                </button>
              </form>

              <div className="mt-4 text-right text-sm">
                <button
                  type="button"
                  onClick={openForgotPassword}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {t('login.link.forgotPassword')}
                </button>
              </div>
            </div>
          )}

          {/* ── Admin tab ──────────────────────────────────────────────────── */}
          {activeTab === 'admin' && !magicLinkSent && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-5">
                {t('login.heading.adminLogin')}
              </h2>

              {adminLoginMode === 'password' && (
                <form onSubmit={handleAdminPasswordSignIn} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700">
                      {t('login.input.label.email')}
                    </label>
                    <input
                      id="adminEmail"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder={t('login.input.placeholder.email')}
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700">
                      {t('login.input.label.password')}
                    </label>
                    <input
                      id="adminPassword"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className={inputClass}
                      placeholder={t('login.input.placeholder.password')}
                      disabled={loading}
                    />
                  </div>
                  <button type="submit" disabled={loading} className={primaryBtn}>
                    {loading ? t('login.button.signingIn') : t('login.button.signIn')}
                  </button>
                  <p className="text-center text-sm text-gray-500">
                    <button type="button" onClick={() => { setAdminLoginMode('magic-link'); setError(''); }} className="text-indigo-600 hover:text-indigo-500">
                      {t('login.link.useMagicLink')}
                    </button>
                  </p>
                </form>
              )}

              {adminLoginMode === 'magic-link' && (
                <form onSubmit={handleMagicLinkSignIn} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      {t('login.input.label.email')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      placeholder={t('login.input.placeholder.email')}
                      disabled={loading}
                    />
                  </div>
                  <button type="submit" disabled={loading} className={primaryBtn}>
                    {loading ? t('login.button.sending') : t('login.button.sendMagicLink')}
                  </button>
                  <p className="text-center text-sm text-gray-500">
                    <button type="button" onClick={() => { setAdminLoginMode('password'); setError(''); }} className="text-indigo-600 hover:text-indigo-500">
                      {t('login.link.usePassword')}
                    </button>
                  </p>
                </form>
              )}
            </div>
          )}

          {/* Magic link sent confirmation */}
          {activeTab === 'admin' && magicLinkSent && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('login.heading.checkEmail')}</h3>
              <p className="text-sm text-gray-600 mb-4">{t('login.text.magicLinkSent')} <strong>{email}</strong></p>
              <p className="text-sm text-gray-500 mb-6">{t('login.text.magicLinkInstructions')}</p>
              <button onClick={() => { setMagicLinkSent(false); setEmail(''); }} className="text-indigo-600 hover:text-indigo-500 font-medium text-sm">
                {t('login.button.useDifferentEmail')}
              </button>
            </div>
          )}

        </div>

        <p className="mt-6 text-center text-sm text-gray-600">
          {t('login.text.needHelp')}{' '}
          <button type="button" onClick={openContactSupport} className="text-indigo-600 hover:text-indigo-500 font-medium">
            {t('login.link.contactSupport')}
          </button>
        </p>
      </div>

      {/* ── Contact support dialog ──────────────────────────────────────────── */}
      {showContactSupport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            {supportSent ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('login.contactSupport.heading.sent')}</h3>
                <p className="text-sm text-gray-600 mb-6">{t('login.contactSupport.text.sent')}</p>
                <button onClick={() => setShowContactSupport(false)} className="text-indigo-600 hover:text-indigo-500 font-medium text-sm">
                  {t('login.contactSupport.button.close')}
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('login.contactSupport.heading.title')}</h3>
                <p className="text-sm text-gray-500 mb-4">{t('login.contactSupport.text.description')}</p>
                <form onSubmit={handleContactSupport} className="space-y-4">
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={4}
                    required
                    placeholder={t('login.contactSupport.input.placeholder.message')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={() => setShowContactSupport(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                      {t('login.contactSupport.button.cancel')}
                    </button>
                    <button type="submit" disabled={supportLoading || !supportMessage.trim()} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 transition-colors">
                      {supportLoading ? t('login.contactSupport.button.sending') : t('login.contactSupport.button.send')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Forgot password dialog ─────────────────────────────────────────── */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            {forgotSent ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('login.forgotPassword.heading.sent')}</h3>
                <p className="text-sm text-gray-600 mb-6">{t('login.forgotPassword.text.sent')}</p>
                <button onClick={() => setShowForgotPassword(false)} className="text-indigo-600 hover:text-indigo-500 font-medium text-sm">
                  {t('login.forgotPassword.button.close')}
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('login.forgotPassword.heading.title')}</h3>
                <p className="text-sm text-gray-500 mb-4">{t('login.forgotPassword.text.description')}</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('login.forgotPassword.input.label.club')}
                    </label>
                    <select
                      value={forgotClubId}
                      onChange={(e) => setForgotClubId(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">{t('login.forgotPassword.input.placeholder.club')}</option>
                      {forgotClubs.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('login.forgotPassword.input.label.memberNumber')}
                    </label>
                    <input
                      type="text"
                      value={forgotMemberNumber}
                      onChange={(e) => setForgotMemberNumber(e.target.value)}
                      required
                      placeholder={t('login.forgotPassword.input.placeholder.memberNumber')}
                      className={inputClass}
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={() => setShowForgotPassword(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                      {t('login.forgotPassword.button.cancel')}
                    </button>
                    <button type="submit" disabled={forgotLoading || !forgotClubId || !forgotMemberNumber.trim()} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 transition-colors">
                      {forgotLoading ? t('login.forgotPassword.button.sending') : t('login.forgotPassword.button.send')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
